import type { LucideIcon } from 'lucide-react'

import {
    Castle,
    Compass,
    HeartPulse,
    Ship,
    Sprout,
    Sun,
    Waves,
    Zap
} from 'lucide-react'

export type NavLink = {
    label: string
    href: string
}

export type Client = {
    name: string
    icon: LucideIcon
}

export type Stat = {
    value: number
    suffix?: string
    label: string
    note: string
}

export type ServiceMotif = 'morph' | 'stream' | 'globe' | 'ripple'

export type Service = {
    number: string
    title: string
    description: string
    capabilities: Array<string>
    motif: ServiceMotif
}

export type Project = {
    client: string
    sector: string
    year: string
    title: string
    metric: string
    metricLabel: string
    tags: Array<string>
    image: string
    imageAlt: string
    focusX: number
    focusY: number
}

export type ProcessStep = {
    number: string
    title: string
    duration: string
    description: string
}

// Absolute anchors so the shared header and footer also work from the 404 and 500 pages.
export const NAV_LINKS: Array<NavLink> = [
    { label: "Work", href: '/#work' },
    { label: "Services", href: '/#services' },
    { label: "Process", href: '/#process' },
    { label: "Studio", href: '/#studio' }
]

export const CLIENTS: Array<Client> = [
    { name: "Vela Energy", icon: Zap },
    { name: "Marea Bank", icon: Waves },
    { name: "Arcadia Health", icon: HeartPulse },
    { name: "Polaris Mobility", icon: Compass },
    { name: "Lumera", icon: Sun },
    { name: "Orto Foods", icon: Sprout },
    { name: "Castello Group", icon: Castle },
    { name: "Nordico Freight", icon: Ship }
]

export const STATS: Array<Stat> = [
    {
        value: 120,
        suffix: '+',
        label: "Products shipped",
        note: "From first versions to platforms serving millions."
    },
    {
        value: 9,
        label: "Years building",
        note: "Founded in Milano in 2016, independent ever since."
    },
    {
        value: 40,
        label: "Specialists",
        note: "Engineers, designers and data people, all senior."
    },
    {
        value: 94,
        suffix: '%',
        label: "Clients who return",
        note: "Most partnerships run for three years or more."
    }
]

export const SERVICES: Array<Service> = [
    {
        number: '01',
        title: "Product design",
        description: "Research, UX and interface design that turns complex workflows into products people enjoy using every day.",
        capabilities: ["Discovery", "UX research", "Design systems", "Prototyping"],
        motif: 'morph'
    },
    {
        number: '02',
        title: "Web and mobile engineering",
        description: "Fast, accessible web apps and native-quality mobile apps, built on modern stacks and tested end to end.",
        capabilities: ["React and Next.js", "iOS and Android", "APIs", "Test automation"],
        motif: 'stream'
    },
    {
        number: '03',
        title: "Cloud and platforms",
        description: "Resilient infrastructure, migrations and DevOps that keep your product online and your costs predictable.",
        capabilities: ["AWS and Google Cloud", "Kubernetes", "CI/CD", "Observability"],
        motif: 'globe'
    },
    {
        number: '04',
        title: "Data and AI",
        description: "Pipelines, analytics and applied machine learning that turn the data you already have into better decisions.",
        capabilities: ["Data engineering", "Dashboards", "LLM features", "Forecasting"],
        motif: 'ripple'
    }
]

export const PROJECTS: Array<Project> = [
    {
        client: "Polaris Mobility",
        sector: "Mobility",
        year: '2025',
        title: "A real-time fleet platform tracking 18,000 vehicles across Europe",
        metric: "−38%",
        metricLabel: "Empty kilometres",
        tags: ["Cloud", "Data", "Web app"],
        image: '/images/work-polaris-mobility.jpg',
        imageAlt: "Aerial view of a winding road between fields",
        focusX: 0.5,
        focusY: 0.42
    },
    {
        client: "Marea Bank",
        sector: "Fintech",
        year: '2026',
        title: "A mobile banking app rebuilt for 1.2 million customers",
        metric: "4.8",
        metricLabel: "App Store rating",
        tags: ["iOS", "Android", "Design system"],
        image: '/images/work-marea-bank.jpg',
        imageAlt: "A hand holding a smartphone",
        focusX: 0.5,
        focusY: 0.5
    },
    {
        client: "Arcadia Health",
        sector: "Healthcare",
        year: '2025',
        title: "Online booking for a network of 64 clinics",
        metric: "90s",
        metricLabel: "Average booking time",
        tags: ["Product design", "Web", "Integrations"],
        image: '/images/work-arcadia-health.jpg',
        imageAlt: "A smiling clinician in a grey uniform",
        focusX: 0.5,
        focusY: 0.28
    },
    {
        client: "Vela Energy",
        sector: "Energy",
        year: '2024',
        title: "Machine learning forecasts for 2.4 GW of wind capacity",
        metric: "−27%",
        metricLabel: "Forecast error",
        tags: ["Data and AI", "Forecasting", "Dashboards"],
        image: '/images/work-vela-energy.jpg',
        imageAlt: "A wind turbine under a clear blue sky above a snowy field",
        focusX: 0.5,
        focusY: 0.3
    }
]

export const PROCESS_STEPS: Array<ProcessStep> = [
    {
        number: '01',
        title: "Discover",
        duration: "1 to 2 weeks",
        description: "Workshops, user interviews and a technical audit. You leave with a clear scope, a plan and a fixed estimate."
    },
    {
        number: '02',
        title: "Design",
        duration: "2 to 4 weeks",
        description: "Flows, prototypes and a design system, tested with real users before a line of production code is written."
    },
    {
        number: '03',
        title: "Build",
        duration: "8 to 16 weeks",
        description: "Weekly releases to a live staging environment, so you can see and steer progress every Friday."
    },
    {
        number: '04',
        title: "Evolve",
        duration: "Ongoing",
        description: "Monitoring, support and a roadmap for what comes next. Most of our clients stay with us for years."
    }
]

export const TESTIMONIAL = {
    quote: "Onda rebuilt our mobile banking app in fourteen weeks without a single hour of downtime. Our rating went from 3.1 to 4.8 and support calls dropped by a third.",
    name: "Giulia Ferraro",
    role: "Chief Digital Officer, Marea Bank",
    image: '/images/testimonial-portrait.jpg',
    imageAlt: "Portrait of Giulia Ferraro"
}
